import React, { useState } from 'react';
import './TaskTable.css';
import EditTaskModal from './EditTaskModal';
import TaskViewModal from './TaskViewModal';
import { updateTask, deleteTask } from './api';
import { formatLocalDate } from './utils/dateUtils';

function TaskTable({
  tasks = [],
  setTasks,
  onCreateSubtask,
  users = [],
  projects = []
}) {
  const [expandedTasks, setExpandedTasks] = useState([]);
  const [modalTask, setModalTask] = useState(null);
  const [isSubtaskEdit, setIsSubtaskEdit] = useState(false);
  const [viewTask, setViewTask] = useState(null);
  const [isSubtaskView, setIsSubtaskView] = useState(false);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [tempStatus, setTempStatus] = useState('');
  const [menuOpenId, setMenuOpenId] = useState(null);

  const toggleSubtasks = (taskId) => {
    setExpandedTasks((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const getUserName = (userId) => {
    const user = users.find((u) => u._id === userId);
    return user ? user.name : '';
  };

  const handleOpenModal = (task, isSubtask = false) => {
    setModalTask(task);
    setIsSubtaskEdit(isSubtask);
    setMenuOpenId(null);
  };

  const handleCloseModal = () => {
    setModalTask(null);
    setIsSubtaskEdit(false);
  };

  const handleOpenView = (task, isSubtask = false) => {
    setViewTask(task);
    setIsSubtaskView(isSubtask);
    setMenuOpenId(null);
  };

  const handleCloseView = () => {
    setViewTask(null);
    setIsSubtaskView(false);
  };

  const handleSaveModal = async (updatedTask) => {
    setTasks((currentTasks) =>
      currentTasks.map((task) =>
        task._id === updatedTask._id ? updatedTask : task
      )
    );

    try {
      await updateTask(updatedTask._id, updatedTask);
    } catch (error) {
      console.error('❌ Error al guardar los cambios desde el modal:', error);
    }

    handleCloseModal();
  };

  const handleStatusClick = (task) => {
    setEditingStatusId(task._id);
    setTempStatus(task.status);
  };

  const handleStatusChange = (e) => {
    setTempStatus(e.target.value);
  };

  const handleStatusBlur = async (task) => {
    const updatedTask = { ...task, status: tempStatus };

    setTasks((currentTasks) =>
      currentTasks.map((t) =>
        t._id === task._id ? updatedTask : t
      )
    );

    try {
      await updateTask(task._id, updatedTask);
    } catch (error) {
      console.error('❌ Error al guardar el nuevo estado:', error);
    }

    setEditingStatusId(null);
  };

  const renderStatusCell = (task) => {
    return editingStatusId === task._id ? (
      <select
        className="status-dropdown"
        value={tempStatus}
        onChange={handleStatusChange}
        onBlur={() => handleStatusBlur(task)}
        autoFocus
      >
        <option value="nueva">Nueva</option>
        <option value="trabajando">Trabajando</option>
        <option value="bloqueada">Bloqueada</option>
        <option value="pendiente">Pendiente</option>
        <option value="completada">Completada</option>
      </select>
    ) : (
      <span
        className={`task-pill ${task.status?.toLowerCase().replace(/\s/g, '-') || ''}`}
        onClick={() => handleStatusClick(task)}
      >
        {task.status}
      </span>
    );
  };

  const handleDeleteTask = async (taskId) => {
    setMenuOpenId(null);

    try {
      await deleteTask(taskId);
      setTasks((currentTasks) =>
        currentTasks.filter((task) => task._id !== taskId)
      );
    } catch (error) {
      const message =
        error.response?.data?.error || 'Error al eliminar la tarea';

      window.alert(message);
    }
  };

  const renderActionMenu = (task, isSubtask = false) => (
    <div className="action-menu">
      <button
        className="dots-button"
        onClick={() => setMenuOpenId(menuOpenId === task._id ? null : task._id)}
      >
        ⋯
      </button>
      {menuOpenId === task._id && (
        <div className="action-dropdown">
          <button
            className="action-edit-button"
            onClick={() => handleOpenView(task, isSubtask)}
          >
            Ver tarea
          </button>

          <button
            className="action-edit-button"
            onClick={() => handleOpenModal(task, isSubtask)}
          >
            Editar
          </button>

          <button
            className="action-delete-button"
            onClick={() => handleDeleteTask(task._id)}
          >
            Eliminar
          </button>
        </div>
      )}
    </div>
  );

  const renderTaskRow = (task) => {
    const isExpanded = expandedTasks.includes(task._id);
    const subtasks = tasks.filter((t) => t.parentTask === task._id);

    return (
      <React.Fragment key={task._id}>
        <tr
          className={`task-row ${
            task.status?.toLowerCase().replace(/\s/g, '-') || ''
          } ${menuOpenId === task._id ? 'menu-open' : ''}`}
        >
          <td>
            <div className={`task-card ${task.status?.toLowerCase().replace(/\s/g, '-') || ''}`}>
              <span
                className="task-title-text"
                title={task.title || ''}
              >
                {task.title?.slice(0, 42)}
              </span>
              {subtasks.length > 0 && (
                <button
                  type="button"
                  className={`toggle-button ${isExpanded ? 'is-expanded' : ''}`}
                  aria-label={
                    isExpanded
                      ? 'Contraer subtareas'
                      : 'Expandir subtareas'
                  }
                  title={
                    isExpanded
                      ? 'Contraer subtareas'
                      : 'Expandir subtareas'
                  }
                  onClick={() => toggleSubtasks(task._id)}
                >
                  <span aria-hidden="true">
                    {isExpanded ? '⌄' : '›'}
                  </span>
                </button>
              )}
            </div>
          </td>
          <td>{renderStatusCell(task)}</td>
          <td>{getUserName(task.responsible)}</td>
          <td>{getUserName(task.validator)}</td>
          <td>{formatLocalDate(task.dueDate)}</td>
          <td>{task.notes?.slice(0, 229)}</td>
          <td>
            <button
              type="button"
              className="btn-subtarea"
              aria-label="Agregar subtarea"
              title="Agregar subtarea"
              onClick={() => onCreateSubtask(task._id)}
            />
          </td>
          <td className="action-cell">{renderActionMenu(task)}</td>
        </tr>

        {isExpanded &&
          subtasks.map((subtask) => (
            <tr
              key={subtask._id}
              className={`subtask-row ${
                subtask.status?.toLowerCase().replace(/\s/g, '-') || ''
              } ${menuOpenId === subtask._id ? 'menu-open' : ''}`}
            >
              <td>
                <span
                  className="subtask-title-text"
                  title={subtask.title || ''}
                >
                  {subtask.title?.slice(0, 42)}
                </span>
              </td>
              <td>{renderStatusCell(subtask)}</td>
              <td>{getUserName(subtask.responsible)}</td>
              <td>{getUserName(subtask.validator)}</td>
              <td>{formatLocalDate(subtask.dueDate)}</td>
              <td>{subtask.notes?.slice(0, 229)}</td>
              <td></td>
              <td className="action-cell">{renderActionMenu(subtask, true)}</td>
            </tr>
          ))}
      </React.Fragment>
    );
  };

  const parentTasks = Array.isArray(tasks) ? tasks.filter((t) => !t.parentTask) : [];

  return (
    <>
      <table className="task-table">
        <thead>
          <tr>
            <th>Título</th>
            <th>Estado</th>
            <th>Responsable</th>
            <th>Validador</th>
            <th>Entrega</th>
            <th>Notas</th>
            <th aria-label="Agregar subtarea"></th>
            <th></th>
          </tr>
        </thead>
        <tbody>{parentTasks.map((task) => renderTaskRow(task))}</tbody>
      </table>

      {modalTask && (
        <EditTaskModal
          task={modalTask}
          isSubtask={isSubtaskEdit}
          onClose={handleCloseModal}
          onSave={handleSaveModal}
          users={users}
          projects={projects}
        />
      )}

      {viewTask && (
        <TaskViewModal
          task={viewTask}
          isSubtask={isSubtaskView}
          onClose={handleCloseView}
          users={users}
          projects={projects}
        />
      )}
    </>
  );
}

export default TaskTable;

